import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import Button from "../../../components/ui/Button";
import { ReactComponent as DeleteBinSvg } from "../../../assets/images/delete-bin-line.svg";
import logo from "../../../assets/images/logo-rewind.png";
import styles from "./ExcerptsCard.module.css";
import studio from "../../../components/modals/StudioModal.module.css";

export interface Excerpt {
    id: string;
    text: string;
}

interface ExcerptsCardProps {
    excerpts: string[];
    onExcerptsChange?: (excerpts: string[]) => void;
    onDeleteExcerpt?: (index: number) => void;
    editable?: boolean;
    showCreateButton?: boolean;
    onCreateClick?: () => void;
}

const ExcerptsCard: React.FC<ExcerptsCardProps> = ({
    excerpts = [],
    onExcerptsChange,
    onDeleteExcerpt,
    editable = false,
    showCreateButton = false,
    onCreateClick,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const modalContentRef = useRef<HTMLDivElement>(null);
    const [localExcerpts, setLocalExcerpts] = useState<string[]>(excerpts);

    useEffect(() => {
        setLocalExcerpts(excerpts);
    }, [excerpts]);

    const handleOutsideClick = useCallback((e: MouseEvent) => {
        if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
            setIsModalOpen(false);
        }
    }, []);

    useEffect(() => {
        if (isModalOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isModalOpen, handleOutsideClick]);

    const MAX_CHARACTERS = 400;

    const handleTextChange = (index: number, text: string) => {
        if (text.length <= MAX_CHARACTERS) {
            const updated = [...localExcerpts];
            updated[index] = text;
            setLocalExcerpts(updated);
            if (onExcerptsChange) {
                onExcerptsChange(updated);
            }
        }
    };

    const handleDelete = (index: number) => {
        if (onDeleteExcerpt) {
            onDeleteExcerpt(index);
        } else {
            const updated = localExcerpts.filter((_, i) => i !== index);
            setLocalExcerpts(updated);
            if (onExcerptsChange) {
                onExcerptsChange(updated);
            }
        }
    };

    const handleCreatePlume = () => {
        if (onCreateClick) {
            onCreateClick();
        } else {
            setIsModalOpen(true);
        }
    };

    return (
        <div className={styles.excerptsCard}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.title}>{t("brandVoices.excerptsTitle") || t("voiceStyles.excerptsTitle")}</h2>
                </div>
            </div>

            <div className={styles.excerptsGrid}>
                {localExcerpts.map((excerpt, index) => (
                    <div key={index} className={styles.excerptCard}>
                        <div className={styles.excerptHeader}>
                            <h3 className={styles.excerptTitle}>
                                {t("brandVoices.excerpt") || t("voiceStyles.excerpt")} {index + 1}
                            </h3>
                            {editable && onDeleteExcerpt && (
                                <Tooltip title={t("delete")} arrow>
                                    <DeleteBinSvg className={styles.deleteButton} onClick={() => handleDelete(index)} />
                                </Tooltip>
                            )}
                        </div>
                        <textarea
                            className={styles.textarea}
                            value={excerpt}
                            onChange={(e) => handleTextChange(index, e.target.value)}
                            placeholder={t("brandVoices.excerptPlaceholder") || t("voiceStyles.excerptPlaceholder")}
                            rows={4}
                            disabled={!editable}
                        />
                        <div className={styles.characterCount}>
                            {excerpt.length}/{MAX_CHARACTERS}
                        </div>
                    </div>
                ))}
            </div>
            {showCreateButton && (
                <div className={styles.createButtonContainer}>
                    <Button
                        variant="primary"
                        label={t("brandVoices.createPlume") || t("voiceStyles.createPlume")}
                        onClick={handleCreatePlume}
                    />
                </div>
            )}
            {isModalOpen &&
                ReactDOM.createPortal(
                    <div className={studio.overlay}>
                        <div className={studio.content} ref={modalContentRef}>
                            <div className={studio.logoWrap}>
                                <img src={logo} alt="Logo" />
                            </div>
                            <h1 className={studio.title}>{t("voiceStyles.plumeCreatedTitle")}</h1>
                            <div style={{ color: "#4f566b", marginBottom: "20px", lineHeight: "1.5" }}>
                                {t("voiceStyles.plumeCreatedMessage")}
                            </div>
                            <div className={studio.buttonsCentered}>
                                <Button
                                    variant="primary"
                                    label={t("voiceStyles.close")}
                                    onClick={() => setIsModalOpen(false)}
                                />
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default ExcerptsCard;
